import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, MapPin, Phone, ArrowRight } from 'lucide-react';
import { CONTACT_INFO } from '../mock';
import { useBooking } from '../context/BookingContext';

const Footer = () => {
  const { openBooking } = useBooking();

  const navLinks = [
    { label: 'Home', to: '/' },
    { label: 'Services', to: '/services' },
    { label: 'Technology', to: '/technology' },
    { label: 'About', to: '/about' },
    { label: 'Articles', to: '/articles' },
    { label: 'Contact', to: '/contact' },
  ];

  const services = [
    'Advanced Facials',
    'Injectables & Fillers',
    'Microneedling + PRP',
    'XERF Structural Tightening',
    'Precision Health',
    'Akira Infusion Therapy',
  ];

  return (
    <footer className="bg-stone-900 text-stone-300">
      {/* CTA Banner */}
      <div className="bg-rose-400 py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3
              className="text-white text-2xl font-light mb-1"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Ready for Your Transformation?
            </h3>
            <p className="text-rose-100 text-sm font-light">
              Get 10% off your first treatment when you book today.
            </p>
          </div>
          <button
            onClick={() => openBooking()}
            className="group bg-white text-rose-400 hover:bg-stone-900 hover:text-rose-300 text-sm tracking-widest uppercase px-10 py-3 transition-all duration-300 flex items-center gap-2 flex-shrink-0"
          >
            Book Now
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </button>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <img
                src="/akira-logo.png"
                alt="Akira Signature Care"
                className="h-16 w-auto"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </div>
            <p className="text-stone-400 text-sm leading-relaxed font-light mb-6">
              A physician-directed medical spa offering advanced aesthetic treatments
              in Alhambra, CA. Where science meets artistry for your natural beauty.
            </p>
            {/* Social */}
            <div className="flex gap-3">
              <a
                href={CONTACT_INFO.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 border border-stone-700 hover:border-rose-400 flex items-center justify-center text-stone-400 hover:text-rose-400 transition-colors duration-300"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href={CONTACT_INFO.social.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 border border-stone-700 hover:border-rose-400 flex items-center justify-center text-stone-400 hover:text-rose-400 transition-colors duration-300 text-xs font-bold"
              >
                TK
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white text-xs tracking-[0.3em] uppercase mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="text-stone-400 text-sm font-light hover:text-rose-400 transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white text-xs tracking-[0.3em] uppercase mb-6">Services</h4>
            <ul className="space-y-3">
              {services.map((s) => (
                <li key={s}>
                  <Link
                    to="/services"
                    className="text-stone-400 text-sm font-light hover:text-rose-400 transition-colors duration-300"
                  >
                    {s}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + Newsletter */}
          <div>
            <h4 className="text-white text-xs tracking-[0.3em] uppercase mb-6">Contact</h4>
            <div className="space-y-3 mb-8">
              <div className="flex gap-3">
                <MapPin className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
                <span className="text-stone-400 text-sm font-light">
                  {CONTACT_INFO.address}<br />{CONTACT_INFO.city}
                </span>
              </div>
              <div className="flex gap-3">
                <Phone className="w-4 h-4 text-rose-400 flex-shrink-0" />
                <span className="text-stone-400 text-sm font-light">{CONTACT_INFO.phone}</span>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Location links — internal-link surface for Greater LA local SEO */}
      <div className="border-t border-stone-800 py-6 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-stone-400 text-[10px] tracking-[0.3em] uppercase mb-3">
            Serving Greater Los Angeles
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-stone-500 text-xs">
            {[
              ['los-angeles', 'Los Angeles'],
              ['east-los-angeles', 'East LA'],
              ['pasadena', 'Pasadena'],
              ['san-gabriel', 'San Gabriel'],
              ['rosemead', 'Rosemead'],
              ['temple-city', 'Temple City'],
              ['monterey-park', 'Monterey Park'],
              ['san-marino', 'San Marino'],
              ['south-pasadena', 'South Pasadena'],
              ['arcadia', 'Arcadia'],
              ['silver-lake', 'Silver Lake'],
              ['highland-park', 'Highland Park'],
              ['eagle-rock', 'Eagle Rock'],
              ['downtown-la', 'Downtown LA'],
            ].map(([slug, label]) => (
              <Link
                key={slug}
                to={`/locations/${slug}`}
                data-testid={`footer-location-${slug}`}
                className="hover:text-rose-400 transition-colors"
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-stone-800 py-6 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-stone-500 text-xs font-light">
            © {new Date().getFullYear()} Akira Signature Care — All Rights Reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" data-testid="footer-privacy-link" className="text-stone-500 hover:text-rose-400 text-xs transition-colors duration-300">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="text-stone-500 hover:text-rose-400 text-xs transition-colors duration-300">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
